import { useFormContext } from "react-hook-form";
import type { QuoteDetailDto } from "@/lib/quotes";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FieldList } from "@/components/field-list";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

// Re-export the values type from this component (the parent used to own it
// via QuoteHeroForm; mirror that shape to keep useForm wiring identical).
export interface QuoteHeroFormValues {
  validUntil: string;
  approvalTypeCode: string;
  termsBody: string;
  purchaseOrder: string;
  description: string;
}

export interface QuoteStatusTermsCardProps {
  quote: QuoteDetailDto;
  editable: boolean;
}

/**
 * Right-column-bottom card. Two sub-cards side-by-side per design:
 *   left  = Quote Status (status, version, linked enquiry, converted-to-job, Valid Until, Approval Type)
 *   right = Terms & Conditions textarea
 * Owns the only form-fields on the Quote tab — Save lives on the page footer.
 */
export function QuoteStatusTermsCard({ quote, editable }: QuoteStatusTermsCardProps) {
  const form = useFormContext<QuoteHeroFormValues>();

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-1 2xl:grid-cols-2 flex-1">
      <Card className="border border-slate-400 rounded-xl overflow-hidden bg-white shadow-sm flex flex-col min-w-0">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-4">
          <CardTitle role="heading" aria-level={3} id="quote-status-heading" className="text-xs font-bold text-slate-800 uppercase tracking-wider">Quote Status</CardTitle>
        </CardHeader>
        <CardContent className="p-4 flex flex-col flex-1 min-w-0">
          <section
            aria-labelledby="quote-status-heading"
            className="space-y-4"
          >
            <FieldList
              className="gap-y-4 text-[#1a2f4c]"
              labelColumnClassName="text-slate-600 font-normal mr-2 break-words"
              items={[
                { label: "Status", value: quote.status },
                { label: "Version", value: `v${quote.quoteVersionNo}` },
                {
                  label: "Linked enquiry",
                  value:
                    quote.enquiryId != null ? (
                      <a href={`/enquiries/${quote.enquiryId}`} className="text-[#1a2f4c] underline">
                        Enquiry #{quote.enquiryId}
                      </a>
                    ) : (
                      "—"
                    ),
                },
                {
                  label: "Converted to job",
                  value:
                    quote.convertedToJobId != null ? (
                      <a href={`/jobs/${quote.convertedToJobId}`} className="text-[#1a2f4c] underline">
                        Job #{quote.convertedToJobId}
                      </a>
                    ) : (
                      "—"
                    ),
                },
              ]}
            />
            <div className="space-y-2 pt-2">
              <Label htmlFor="quote-valid-until" className="text-sm font-normal text-slate-600">
                Valid until
              </Label>
              <Input id="quote-valid-until" type="date" disabled={!editable} {...form.register("validUntil")} className="text-[#1a2f4c] w-full min-w-0" />
            </div>
            <div className="space-y-2 pt-2">
              <Label htmlFor="quote-approval-type" className="text-sm font-normal text-slate-600">
                Approval type
              </Label>
              <Input id="quote-approval-type" disabled={!editable} {...form.register("approvalTypeCode")} className="text-[#1a2f4c] w-full min-w-0" />
            </div>
          </section>
        </CardContent>
      </Card>

      <Card className="border border-slate-400 rounded-xl overflow-hidden bg-white shadow-sm flex flex-col min-w-0">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-4">
          <CardTitle role="heading" aria-level={3} id="quote-terms-heading" className="text-xs font-bold text-slate-800 uppercase tracking-wider">Terms &amp; Conditions</CardTitle>
        </CardHeader>
        <CardContent className="p-4 flex flex-col flex-1 min-w-0">
          <section className="space-y-4 flex flex-col flex-1" aria-labelledby="quote-terms-heading">
            <Label htmlFor="quote-terms-body" className="text-sm font-normal text-slate-600">
              Terms body
            </Label>
            <Textarea
              id="quote-terms-body"
              placeholder="Terms preview"
              disabled={!editable}
              {...form.register("termsBody")}
              className="text-[#1a2f4c] flex-1 resize-none min-h-[200px]"
            />
          </section>
        </CardContent>
      </Card>
    </div>
  );
}
